import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { usePage } from '@inertiajs/react';

interface ToastItem {
    id: number;
    title: string;
    message: string;
    leaving?: boolean;
}

interface ToastContextValue {
    showToast: (title: string, message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let idCounter = 0;

/**
 * Ported from showToast() in admin_version_1.html. Same trigger pattern:
 * bottom-right stack, dark card, bell icon, visible ~4.5s then fades. Wrap
 * the whole app in this once (see app.tsx / AuthenticatedLayout below) and
 * call useToast().showToast(title, message) anywhere — same call shape as
 * the prototype's global showToast(title, message).
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
    const [toasts, setToasts] = useState<ToastItem[]>([]);

    const showToast = useCallback((title: string, message: string) => {
        const id = ++idCounter;
        setToasts((prev) => [...prev, { id, title, message }]);

        // Start the fade at 4.5s, then unmount after the .3s transition —
        // matches the prototype's setTimeout(...,4500) -> opacity fade -> remove().
        setTimeout(() => {
            setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
            setTimeout(() => {
                setToasts((prev) => prev.filter((t) => t.id !== id));
            }, 300);
        }, 4500);
    }, []);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            <div className="fixed bottom-5 right-5 z-[200] flex flex-col gap-2 max-w-[320px] pointer-events-none">
                {toasts.map((t) => (
                    <div
                        key={t.id}
                        className={`bg-slate-950 text-white rounded-xl px-3.5 py-3 text-[12.5px] shadow-2xl flex gap-2.5 items-start transition-opacity duration-300 ${
                            t.leaving ? 'opacity-0' : 'opacity-100 animate-toast-in'
                        }`}
                    >
                        <span>🔔</span>
                        <div>
                            <b>{t.title}</b>
                            <div className="opacity-85 mt-0.5">{t.message}</div>
                        </div>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
}

export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast must be used within a ToastProvider');
    return ctx;
}

/**
 * Auto-surfaces every back()->with('success'/'error', ...) flash as a toast —
 * this is what was missing. Every controller that returns back()->with('error', ...)
 * (customers.store/update duplicate checks, enquiries reassign/status errors, etc.)
 * now actually reaches the user instead of silently doing nothing.
 * Mount this once, inside <ToastProvider>, e.g. in AuthenticatedLayout.
 */
export function FlashToaster() {
    const { showToast } = useToast();
    const { flash } = usePage().props as any;

    useEffect(() => {
        if (flash?.success) showToast('Success', flash.success);
        if (flash?.error) showToast('Error', flash.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flash?.success, flash?.error]);

    return null;
}